import { HttpClient } from '@angular/common/http';
import { Component, OnInit, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { DialogStatusComponent } from 'src/app/dialog/dialog-status/dialog-status.component';
import { AppService, GRSSResponse } from 'src/app/_services/app.service';
import { AuthenticationComponent } from '../authentication/authentication.component';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { error } from 'console';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit {

  user: UserLogin;
  isPasswordVisible;
  backgroundUrl: string = '';

  enableAzureLogin: boolean = false;
  hasLoggedIn: boolean = false;

  constructor(private translate: TranslateService, private msalService: MsalService, private router: Router, private http: HttpClient, private dialog: MatDialog, @Inject('BASE_URL') public baseUrl: string, private service: AppService) {
    this.service.SetTranslation('user');
  }

  ngOnInit() {
    this.checkAzureLogin();
    this.isPasswordVisible = false;
    this.backgroundUrl = this.baseUrl + 'styles/login_background.jpg';

    if (this.service.currentUser.isTestingEnabled === true) {
      this.backgroundUrl = this.baseUrl + 'styles/login_background-uat.jpg';
    }
    this.InitializeLogin();
  }

  /**
   * Checks and redirects user to azure login
   */
  checkAzureLogin() {
    this.http.get(this.baseUrl + 'api/common/getSetting/EnableAzureLogin', { responseType: 'text' }).subscribe(async result => {
      if (result) {
        this.enableAzureLogin = this.service.getBoolean(result);
        if (this.enableAzureLogin) {
          await this.msalService.instance.initialize();
          await this.msalService.instance.handleRedirectPromise();
          let account = this.msalService.instance.getActiveAccount();
          if (account) {
            this.redirectToAzureLogin(account);
          } else {
            this.onClickAzureLogin();
          }
        }
      }
    }, error => {
      console.error(error);
    });
  }

  /**
   * Logs out from Azure
   */
  async onClickAzureLogout() {
    await this.msalService.instance.handleRedirectPromise();
    const details = this.msalService.instance.getActiveAccount();
    if (details) {
      this.msalService.instance.logout();
    }
  }

  async onClickAzureLogin() {
    await this.msalService.instance.handleRedirectPromise();
    this.msalService.loginPopup().subscribe((response: AuthenticationResult) => {
      this.msalService.instance.setActiveAccount(response.account);
      this.redirectToAzureLogin(response.account);
    }, error => {
      console.log(error);
      this.onClickAzureLogout();
    })
  }

  /**
   * Checks and redirects to Azure login page
   */
  redirectToAzureLogin(account: AccountInfo) {
    this.hasLoggedIn = true;
    this.http.post<GRSSResponse>(this.baseUrl + 'api/auth/msLogin', {
      Email: account.username,
      LocalAccountId: account.localAccountId
    }).subscribe(result => {
      if (result.statusCode == 1) {
        this.service.CheckAuth().then(e => {
          this.router.navigate([this.service.currentUser.defaultUrl]);
        });
      } else {
        this.service.SaveStatus(false, 'Auth.Messages.msgHeaderLogin', result.statusMessage);
      }
    }, error => {
      console.error(error);
    });
  }

  /**
   *  Submit button event in the login form
   * @param loginForm Login form values
   */
  OnLoginSubmitted(loginForm: NgForm) {
    if (loginForm.valid) {
      const formDetails = Object.assign({}, loginForm.value);
      this.http.post<LoginResponse>(this.baseUrl + 'api/auth/login', this.user).subscribe(result => {
        if (result.responseStatus.statusCode === 1) {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = '60%';
          dialogConfig.data = result;
          dialogConfig.position = { top: '5%' };

          if (result.isTemporary) {
            this.dialog.open(ResetPasswordComponent, dialogConfig);
          } else if (result.isExpired) {
            this.dialog.open(DialogStatusComponent, {
              disableClose: true,
              data: {
                title: this.translate.instant('Auth.Messages.msgHeaderPWExpired'),
                message: this.translate.instant('Auth.Messages.msgParaPWExpired'),
                innerHTML: true
              },
              position: { top: '5%' }
            }).afterClosed().subscribe(res => {
              this.dialog.open(ResetPasswordComponent, dialogConfig);
            });
          } else if (result.expiresIn === 1) {
            this.dialog.open(DialogStatusComponent, {
              disableClose: true,
              data: {
                title: this.translate.instant('Auth.Messages.msgHeaderPWWarning'),
                message: this.translate.instant('Auth.Messages.msgParaPWExpOne'),
                innerHTML: true
              },
              position: { top: '5%' }
            }).afterClosed().subscribe(res => {
              this.dialog.open(ResetPasswordComponent, dialogConfig);
            });
          } else if (result.challengeQuestion || result.twoFactor) {
            this.service.userTemp = this.user;
            this.dialog.open(AuthenticationComponent, dialogConfig).afterClosed().subscribe(res => {
              if (result.expiresIn > 0) {
                this.DisplayExpiration(result, dialogConfig);
              } else {
                this.service.CheckAuth().then(e => {
                  this.router.navigate([this.service.currentUser.defaultUrl]);
                });
              }
            });
          } else if (result.expiresIn > 0) {
            this.DisplayExpiration(result, dialogConfig);
          } else {
            this.service.CheckAuth().then(e => {
              this.router.navigate([this.service.currentUser.defaultUrl]);
            });
          }
        } else {
          this.service.SaveStatus(false, 'Auth.Messages.msgHeaderLogin', result.responseStatus.statusMessage);
        }
      });
    }
  }

  /**
   * Check and provide relevant login screen
   * @param result Login response
   * @param dialogConfig Dialog configurations
   */
  DisplayExpiration(result: LoginResponse, dialogConfig) {
    this.dialog.open(DialogConfirmComponent, {
      disableClose: true,
      data: {
        title: this.translate.instant('Auth.Messages.msgHeaderPWWarning'),
        message: String(this.translate.instant('Auth.Messages.msgParaPWExpMul')).replace('@days', result.expiresIn.toString()),
        innerHTML: true
      },
      position: { top: '5%' }
    }).afterClosed().subscribe(res => {
      if (res === true) {
        this.dialog.open(ResetPasswordComponent, dialogConfig);
      } else {
        this.service.CheckAuth().then(e => {
          this.router.navigate([this.service.currentUser.defaultUrl]);
        });
      }
    });
  }


  /** Forgot password click event for spin */
  OnForgotClicked() {
    // $('._FlipCardInner').css('transform', 'rotateY(180deg)');
    const element = document.getElementsByClassName('flip-card-inner')[0] as HTMLElement;
    element.style.transform = 'rotateY(180deg)';
  }

  /**
   * Submit button event in the foorgot password form
   * @param forgotForm forgot form values
   */
  OnForgotSubmitted(forgotForm: NgForm) {
    if (forgotForm.valid) {
      this.service.showSpinner();
      this.http.get<GRSSResponse>(this.baseUrl + 'api/auth/forgotPassword/' + this.user.username).subscribe(result => {
        this.service.SaveStatus(false, 'Auth.Messages.msgHeaderPWFrgt', result.statusMessage);
      }, error => {
        console.error(error);
      }).add(() => {
        this.service.hideSpinner();
      });
    }
  }

  /** Back button event for spin */
  OnBackClicked() {
    const element = document.getElementsByClassName('flip-card-inner')[0] as HTMLElement;
    element.style.transform = '';
  }

  /** Initializes the form values */
  InitializeLogin() {
    this.user = {
      password: '',
      username: ''
    };
  }
}

/** Interface to access user login details */
export interface UserLogin {
  username: string;
  password: string;
}

/** Interface to map server response */
export interface LoginResponse {
  responseStatus: GRSSResponse;
  userId: string;
  twoFactor: boolean;
  challengeQuestion: boolean;
  question: string;
  userAnswer: string;
  userCode: string;
  isTemporary: boolean;
  isExpired: boolean;
  expiresIn: number;
}

